import type { ComponentProps } from 'react';
import { useContext } from 'react';
import MUICheckbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context';
import styles from './style/Checkbox.module.css';

type Props = {
  className?: string;
  disabled?: boolean;
  checked: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  style?: React.CSSProperties;
  checkedColor?: string;
};

export const Checkbox = ({ style = {}, ...props }: Props) => {
  const themeCtx = useContext(AppThemingContext);

  if (!themeCtx) {
    throw new Error('Checkbox must be used within an AppThemingContext provider');
  }

  const { theme, palette } = themeCtx;

  const checkedColor = props.checkedColor ?? (theme?.enabled ? palette.primary.main : `var(--pri-02)`);

  return (
    <MUICheckbox
      className={props.className}
      disableRipple={true}
      disabled={props.disabled}
      color="default"
      sx={{
        color: 'var(--gray-d)',
        padding: 0,
        marginRight: '6px',
        flexShrink: 0,
        [`&.Mui-checked`]: {
          color: checkedColor,
        },
        [`&.Mui-disabled`]: {
          color: 'var(--gray-l)',
        },
      }}
      style={style}
      checked={props.checked ?? false}
      onChange={props.onChange}
    />
  );
};

Checkbox.displayName = 'Checkbox';

export const CheckboxLabel = ({
  className,
  label = '',
  checked = false,
  disabled = false,
  onChange = () => {},
  style,
}: CheckboxLabelProps) => {
  return (
    <FormControlLabel
      className={className}
      sx={{
        marginLeft: 0,
        [`& .MuiFormControlLabel-label`]: {
          fontSize: 16,
          fontFamily: 'var(--font-reg)',
          width: '100%',
        },
      }}
      control={
        <MUICheckbox
          className={checked ? styles.checked : styles.unchecked}
          checked={checked}
          style={{
            marginRight: 10,
          }}
          disabled={disabled}
          onChange={onChange}
        />
      }
      label={label}
      style={style}
    />
  );
};

type CheckboxLabelProps = Pick<
  ComponentProps<typeof FormControlLabel>,
  'checked' | 'label' | 'style' | 'disabled'
> &
  Pick<ComponentProps<typeof MUICheckbox>, 'onChange'> & {
    className?: string;
  };
